import { useEffect, useRef, useState, useCallback } from "react";
import DesignationsRemove from "../../../../assets/svgs/DesignationsRemove";
import DropdownCaret from "../../../../assets/svgs/DropdownCaret";
import { PD_ACTIONS, PD_AUTOCOMPLETE } from "../../../../constants/professionalDesignations";

const DesignationAutocomplete = ({
                                     id,
                                     value,
                                     currentDesignationId,
                                     availableDesignations,
                                     selectedDesignationIds,
                                     onChange,
                                     onSelect,
                                     onClear,
                                     onValidationError,
                                     hasError,
                                     errorText,
                                     disabled,
                                     label,
                                 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || "");
    const [filteredDesignations, setFilteredDesignations] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [showTooltip, setShowTooltip] = useState(false);
    const [hoveredDropdownItem, setHoveredDropdownItem] = useState(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const justSelectedRef = useRef(false);
    const searchTermRef = useRef(searchTerm);
    const selectedDesignationIdsRef = useRef(selectedDesignationIds);
    const valueRef = useRef(value);
    const onValidationErrorRef = useRef(onValidationError);
    const currentDesignationIdRef = useRef(currentDesignationId);
    const dropdownId = `${id}-listbox`;
    const errorId = `${id}-error`;

    searchTermRef.current = searchTerm;
    selectedDesignationIdsRef.current = selectedDesignationIds;
    valueRef.current = value;
    onValidationErrorRef.current = onValidationError;
    currentDesignationIdRef.current = currentDesignationId;

    const isValueSelected = value && searchTerm === value && availableDesignations.some(
        (d) => d.name === value
    );
    
    const needsTruncation = isValueSelected && searchTerm.length > PD_ACTIONS.MAX_DISPLAY_LENGTH;
    const displayValue = needsTruncation 
        ? searchTerm.substring(0, PD_ACTIONS.MAX_DISPLAY_LENGTH) + "..."
        : searchTerm;

    // Get the ID of the currently highlighted option for aria-activedescendant
    const getActiveDescendantId = useCallback(() => {
        if (highlightedIndex >= 0 && highlightedIndex < filteredDesignations.length) {
            return `${id}-option-${filteredDesignations[highlightedIndex].id}`;
        }
        return undefined;
    }, [highlightedIndex, filteredDesignations, id]);

    useEffect(() => {
        const available = availableDesignations.filter(
            (d) =>
                !selectedDesignationIds.includes(d.id) ||
                d.id === currentDesignationId
        );
        if (searchTerm.trim() === "") {
            setFilteredDesignations(available);
        } else {
            setFilteredDesignations(
                available.filter((d) =>
                    d.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
        // Reset highlighted index when filtered results change
        setHighlightedIndex(-1);
    }, [searchTerm, availableDesignations, selectedDesignationIds, currentDesignationId]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setSearchTerm(value || "");
    }, [value]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const highlightedElement = listRef.current.querySelector(
                `[data-index="${highlightedIndex}"]`
            );
            if (highlightedElement) {
                highlightedElement.scrollIntoView({
                    block: "nearest",
                    behavior: "smooth",
                });
            }
        }
    }, [highlightedIndex]);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        onChange(val);
        setIsOpen(true);
        setHighlightedIndex(-1);
        if (onValidationError) {
            const trimmed = val.trim();
            if (!trimmed) {
                onValidationError("");
            } else {
                const exactMatch = availableDesignations.find(
                    (d) => d.name.toLowerCase() === trimmed.toLowerCase()
                );
                if (exactMatch) {
                    if (selectedDesignationIdsRef.current.includes(exactMatch.id) && exactMatch.id !== currentDesignationIdRef.current) {
                        onValidationError(PD_AUTOCOMPLETE.VALIDATION_MESSAGES.ALREADY_SELECTED);
                        setIsOpen(false);
                        return;
                    }
                    onValidationError("");
                } else {
                    const partialMatch = availableDesignations.find(
                        (d) => d.name.toLowerCase().includes(trimmed.toLowerCase())
                    );
                    if (!partialMatch) {
                        onValidationError(PD_AUTOCOMPLETE.VALIDATION_MESSAGES.INVALID_DESIGNATION);
                    } else {
                        onValidationError("");
                    }
                }
            }
        }
    };

    const handleBlur = () => {
        setTimeout(() => {
            if (justSelectedRef.current) {
                justSelectedRef.current = false;
                return;
            }
            const trimmedSearch = searchTermRef.current.trim();
            if (!trimmedSearch) {
                if (onValidationErrorRef.current) {
                    onValidationErrorRef.current(PD_AUTOCOMPLETE.VALIDATION_MESSAGES.INVALID_DESIGNATION);
                }
                return;
            }
            const matchedDesignation = availableDesignations.find(
                (d) => d.name.toLowerCase() === trimmedSearch.toLowerCase()
            );
            if (!matchedDesignation) {
                if (onValidationErrorRef.current) {
                    onValidationErrorRef.current(PD_AUTOCOMPLETE.VALIDATION_MESSAGES.INVALID_DESIGNATION);
                }
            } else if (
                selectedDesignationIdsRef.current.includes(matchedDesignation.id) &&
                matchedDesignation.id !== currentDesignationIdRef.current
            ) {
                if (onValidationErrorRef.current) {
                    onValidationErrorRef.current(PD_AUTOCOMPLETE.VALIDATION_MESSAGES.ALREADY_SELECTED);
                }
            } else {
                handleSelect(matchedDesignation);
            }
        }, 200);
    };

    const handleSelect = (designation) => {
        if (designation && designation.id !== undefined) {
            justSelectedRef.current = true;
            setSearchTerm(designation.name);
            onSelect(designation);
            setIsOpen(false);
            setHighlightedIndex(-1);
        }
    };

    const handleClear = () => {
        setSearchTerm("");
        onClear();
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        const itemCount = filteredDesignations.length;
        const { KEYBOARD_KEYS } = PD_AUTOCOMPLETE;

        switch (e.key) {
            case KEYBOARD_KEYS.ARROW_DOWN:
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                    setHighlightedIndex(0);
                } else if (itemCount > 0) {
                    setHighlightedIndex((prev) =>
                        prev < itemCount - 1 ? prev + 1 : 0
                    );
                }
                break;

            case KEYBOARD_KEYS.ARROW_UP:
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                    setHighlightedIndex(itemCount - 1);
                } else if (itemCount > 0) {
                    setHighlightedIndex((prev) =>
                        prev > 0 ? prev - 1 : itemCount - 1
                    );
                }
                break;

            case KEYBOARD_KEYS.ENTER:
                if (isOpen && highlightedIndex >= 0 && highlightedIndex < itemCount) {
                    e.preventDefault();
                    handleSelect(filteredDesignations[highlightedIndex]);
                }
                break;

            case KEYBOARD_KEYS.TAB:
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;

            case KEYBOARD_KEYS.ESCAPE:
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                inputRef.current?.focus();
                break;

            case KEYBOARD_KEYS.HOME:
                if (isOpen && itemCount > 0) {
                    e.preventDefault();
                    setHighlightedIndex(0);
                }
                break;

            case KEYBOARD_KEYS.END:
                if (isOpen && itemCount > 0) {
                    e.preventDefault();
                    setHighlightedIndex(itemCount - 1);
                }
                break;

            default:
                break;
        }
    };

    const handleItemMouseEnter = (index) => {
        setHighlightedIndex(index);
    };

    const highlightMatch = (text, search) => {
        if (!search.trim()) return text;
        const regex = new RegExp(
            `(${search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
            "gi"
        );
        const parts = text.split(regex);
        return parts.map((part, idx) =>
            regex.test(part) ? (
                <span key={idx} className="highlight">{part}</span>
            ) : (
                part
            )
        );
    };

    return (
        <div className="designation-autocomplete-wrapper" ref={wrapperRef}>
            <div 
                className="input-with-icons"
                onMouseEnter={() => needsTruncation && setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                {needsTruncation && showTooltip && (
                    <div className="designation-tooltip">
                        {searchTerm}
                    </div>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    id={id}
                    role="combobox"
                    className={`form-control ${hasError ? "is-invalid" : ""}`}
                    placeholder={PD_AUTOCOMPLETE.PLACEHOLDER}
                    value={needsTruncation ? displayValue : searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    autoComplete="off"
                    aria-label={label || PD_AUTOCOMPLETE.DEFAULT_LABEL}
                    aria-haspopup="listbox"
                    aria-controls={dropdownId}
                    aria-autocomplete="list"
                    aria-expanded={isOpen}
                    aria-invalid={hasError || undefined}
                    aria-describedby={hasError ? errorId : undefined}
                    aria-activedescendant={getActiveDescendantId()}
                />
                <div className="input-icons">
                    {searchTerm && !disabled && (
                        <button
                            type="button"
                            className="clear-btn"
                            onClick={handleClear}
                            aria-label={PD_AUTOCOMPLETE.ARIA_LABELS.CLEAR_SELECTION}
                        >
                            <DesignationsRemove />
                        </button>
                    )}
                    <button
                        type="button"
                        className="dropdown-toggle-btn"
                        onClick={() => {
                            if (!disabled) {
                                setIsOpen(!isOpen);
                                inputRef.current?.focus();
                            }
                        }}
                        disabled={disabled}
                        aria-label={isOpen ? PD_AUTOCOMPLETE.ARIA_LABELS.CLOSE_DROPDOWN : PD_AUTOCOMPLETE.ARIA_LABELS.OPEN_DROPDOWN}
                        tabIndex={-1}
                    >
                        <DropdownCaret isOpen={isOpen} />
                    </button>
                </div>
            </div>

            {isOpen && filteredDesignations.length > 0 && (
                <ul
                    className="designation-dropdown"
                    role="listbox"
                    id={dropdownId}
                    ref={listRef}
                >
                    {filteredDesignations.map((designation, index) => {
                        const optionId = `${id}-option-${designation.id}`;
                        const isHighlighted = index === highlightedIndex;
                        const isSelected = designation.name === value;
                        const needsDropdownTruncation = designation.name.length > PD_ACTIONS.MAX_DROPDOWN_DISPLAY_LENGTH;
                        const truncatedName = needsDropdownTruncation 
                            ? designation.name.substring(0, PD_ACTIONS.MAX_DROPDOWN_DISPLAY_LENGTH) + "..."
                            : designation.name;
                        return (
                            <li
                                key={designation.id}
                                id={optionId}
                                role="option"
                                data-index={index}
                                aria-selected={isSelected}
                                className={`dropdown-item${isSelected ? " selected" : ""}${isHighlighted ? " highlighted" : ""}`}
                                onClick={() => handleSelect(designation)}
                                onMouseEnter={() => {
                                    handleItemMouseEnter(index);
                                    if (needsDropdownTruncation) {
                                        setHoveredDropdownItem(designation.id);
                                    }
                                }}
                                onMouseLeave={() => setHoveredDropdownItem(null)}
                            >
                                {needsDropdownTruncation && hoveredDropdownItem === designation.id && (
                                    <div className="dropdown-item-tooltip">
                                        {designation.name}
                                    </div>
                                )}
                                {needsDropdownTruncation 
                                    ? highlightMatch(truncatedName, searchTerm)
                                    : highlightMatch(designation.name, searchTerm)
                                }
                            </li>
                        );
                    })}
                </ul>
            )}

            {hasError && (
                <div id={errorId} className="invalid-feedback" role="alert">{errorText}</div>
            )}
        </div>
    );
};

export default DesignationAutocomplete;
